import React, { useState, useEffect, useRef } from "react";
import "./AdminBulkUpload.css";
import { useAuth } from "../api/AuthContext";
import {
  compressImage,
  uploadToR2,
  isR2Configured,
  getCompressedFileInfo,
  listExistingKeys,
  getPublicUrlForKey,
} from "../Utils/r2Upload";
import { extractBaseName } from "../Utils/imageMatching";
import { Link } from "react-router-dom";

export default function AdminBulkUpload() {
  const { api } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({ total: 0, processed: 0, matched: 0, failed: 0, syncFailed: 0, skipped: 0 });
  const [logs, setLogs] = useState([]);
  const fileInputRef = useRef(null);
  const logContainerRef = useRef(null);

  // Fetch all products from DB on load
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      addLog("Fetching products from database...", "info");
      const res = await api.get("/products");
      const products = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setAllProducts(products);
      addLog(`Loaded ${products.length} products from database ready for sync.`, "success");
    } catch (err) {
      addLog("Failed to fetch products from backend: " + err.message, "error");
    }
  };

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, message, type }]);
    if (logContainerRef.current) {
      setTimeout(() => {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }, 50);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSelectedFiles(files);
    setStats({ total: files.length, processed: 0, matched: 0, failed: 0, syncFailed: 0, skipped: 0 });
    setProgress(0);
    addLog(`Selected ${files.length} images from device gallery.`, "info");
  };

  // Process queue 2 at a time (RAM SAFE for mobile)
  const startBulkSync = async () => {
    if (!selectedFiles.length) {
      alert("Please select images first.");
      return;
    }

    if (!isR2Configured()) {
      alert(
        "Cloudflare R2 is not fully configured yet.\nPlease add REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, and REACT_APP_R2_SECRET_ACCESS_KEY to your .env file."
      );
      return;
    }

    setIsProcessing(true);
    addLog(`Starting bulk compression & Cloudflare upload for ${selectedFiles.length} photos...`, "info");

    // Fetch the list of objects already in R2 ONCE up front, instead of
    // checking per file — one list call instead of hundreds of PUT/HEAD
    // calls. Files already present get skipped (still attempts DB sync,
    // so a retry can pick up files that uploaded fine last time but only
    // failed the DB-link step).
    let existingKeys = new Set();
    try {
      addLog("Checking Cloudflare R2 for images already uploaded...", "info");
      existingKeys = await listExistingKeys("products/");
      addLog(`Found ${existingKeys.size} images already in R2 — matching ones will be skipped.`, "info");
    } catch (err) {
      addLog(`Could not check existing R2 files (${err.message}) — will upload everything.`, "error");
    }
    const EXTENSION_CANDIDATES = ["webp", "jpg", "jpeg", "png"];
    const findExistingKey = (baseName) =>
      EXTENSION_CANDIDATES.map((ext) => `products/${baseName}.${ext}`).find((k) => existingKeys.has(k));

    let processedCount = 0;
    let matchedCount = 0;
    let failedCount = 0;
    let syncFailedCount = 0;
    let skippedCount = 0;

    // Concurrency Worker Pool: 2 files at a time
    const CONCURRENCY = 2;
    const queue = [...selectedFiles];

    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        const baseName = extractBaseName(file.name);
        const existingKey = findExistingKey(baseName);

        let publicUrl;
        if (existingKey) {
          // Already in R2 — don't pay for another compress + upload.
          publicUrl = getPublicUrlForKey(existingKey);
          skippedCount++;
          processedCount++;
          addLog(`${baseName} already in R2 (${existingKey}) — skipping re-upload.`, "info");
        } else {
          // Upload to R2 first — a failure here means the photo never made
          // it to storage, so it belongs in "failed".
          try {
            addLog(`Compressing ${file.name}...`, "info");
            const compressedBlob = await compressImage(file, 1200, 0.82);
            const { contentType, extension } = getCompressedFileInfo(compressedBlob);
            const destinationKey = `products/${baseName}.${extension}`;

            addLog(`Uploading ${baseName}.${extension} to Cloudflare R2...`, "info");
            publicUrl = await uploadToR2(compressedBlob, destinationKey, contentType);
            processedCount++;
          } catch (err) {
            failedCount++;
            addLog(`Failed ${file.name}: ${err.message}`, "error");

            const pct = Math.round(((processedCount + failedCount) / selectedFiles.length) * 100);
            setProgress(pct);
            setStats({
              total: selectedFiles.length,
              processed: processedCount,
              matched: matchedCount,
              failed: failedCount,
              syncFailed: syncFailedCount,
              skipped: skippedCount,
            });
            continue;
          }
        }

        // The photo is safely in R2 at this point. A DB-sync failure below
        // is a separate, recoverable problem — it should not be counted as
        // an upload failure.
        try {
          // Find matching product in DB
          const matchedProduct = allProducts.find((p) => {
            const images = p.productImages || p.ProductImages || [];
            return images.some((imgUrl) => {
              const dbBase = extractBaseName(imgUrl);
              return dbBase && (dbBase === baseName || imgUrl.toLowerCase().includes(baseName));
            });
          });

          if (matchedProduct) {
            // Update productImages array with new Cloudflare URL
            const existingImages = matchedProduct.productImages || matchedProduct.ProductImages || [];
            const updatedImages = existingImages.map((imgUrl) => {
              const dbBase = extractBaseName(imgUrl);
              if (dbBase === baseName || imgUrl.toLowerCase().includes(baseName)) {
                return publicUrl;
              }
              return imgUrl;
            });

            // Save to DB via API
            await api.put(`/products/${matchedProduct.productId}/images`, updatedImages);

            matchedCount++;
            addLog(
              `Matched & Updated Product #${matchedProduct.productId} (${matchedProduct.productName}) with ${publicUrl}`,
              "success"
            );
          } else {
            addLog(`Uploaded ${baseName} to R2 (No matching product found in DB yet).`, "info");
          }
        } catch (err) {
          syncFailedCount++;
          addLog(`Uploaded ${baseName} to R2, but DB sync failed: ${err.message}`, "error");
        }

        const pct = Math.round(((processedCount + failedCount) / selectedFiles.length) * 100);
        setProgress(pct);
        setStats({
          total: selectedFiles.length,
          processed: processedCount,
          matched: matchedCount,
          failed: failedCount,
          syncFailed: syncFailedCount,
          skipped: skippedCount,
        });
      }
    };

    // Run 2 workers in parallel
    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);

    setIsProcessing(false);
    addLog(
      `Finished! Processed: ${processedCount} (${skippedCount} skipped, already in R2), Matched to DB: ${matchedCount}, Sync Failed: ${syncFailedCount}, Failed: ${failedCount}`,
      "success"
    );
    alert(
      `Bulk Upload Complete!\n${processedCount} photos present in Cloudflare R2 (${skippedCount} skipped as duplicates).\n${matchedCount} products updated in database.${
        syncFailedCount ? `\n${syncFailedCount} uploaded but failed to sync to DB — retry these.` : ""
      }`
    );
  };

  return (
    <div className="bulk-upload-container">
      <div className="bulk-upload-wrapper">
        <header className="bulk-header">
          <div>
            <h1 className="bulk-title">Aminii Cloud Sync Engine</h1>
            <p className="bulk-subtitle">Direct Mobile Bulk Image Uploader & DB Auto-Matcher</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Link to="/AdminAminii" className="bulk-badge" style={{ textDecoration: "none" }}>
              ← Admin
            </Link>
            <Link to="/admin/image-linker" className="bulk-badge" style={{ textDecoration: "none" }}>
              Link Unmatched Photos →
            </Link>
            <span className="bulk-badge">Cloudflare R2</span>
          </div>
        </header>

        {/* Upload Card */}
        <div className="bulk-card">
          <input
            type="file"
            multiple
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelect}
            disabled={isProcessing}
          />

          <div
            className="bulk-dropzone"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <svg
              className="bulk-dropzone-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <h3 className="bulk-dropzone-title">
              {selectedFiles.length > 0
                ? `${selectedFiles.length} Photos Selected`
                : "Tap to Select Photos from Mobile Gallery"}
            </h3>
            <p className="bulk-dropzone-desc">
              Select all 100+ product photos at once. Photos will be automatically compressed & linked.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="bulk-stats-grid">
            <div className="bulk-stat-box">
              <div className="bulk-stat-val">{stats.total}</div>
              <div className="bulk-stat-label">Selected</div>
            </div>
            <div className="bulk-stat-box">
              <div className="bulk-stat-val" style={{ color: "#25d366" }}>
                {stats.processed}
              </div>
              <div className="bulk-stat-label">Uploaded</div>
            </div>
            <div className="bulk-stat-box">
              <div className="bulk-stat-val" style={{ color: "#d4af37" }}>
                {stats.matched}
              </div>
              <div className="bulk-stat-label">DB Synced</div>
            </div>
            <div className="bulk-stat-box">
              <div className="bulk-stat-val" style={{ color: "#ff5555" }}>
                {stats.failed}
              </div>
              <div className="bulk-stat-label">Failed</div>
            </div>
            <div className="bulk-stat-box">
              <div className="bulk-stat-val" style={{ color: "#ff9955" }}>
                {stats.syncFailed}
              </div>
              <div className="bulk-stat-label">Sync Failed</div>
            </div>
            <div className="bulk-stat-box">
              <div className="bulk-stat-val" style={{ color: "#8888ff" }}>
                {stats.skipped}
              </div>
              <div className="bulk-stat-label">Skipped (Dup)</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bulk-progress-bar-bg">
            <div className="bulk-progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>

          {/* Action Button */}
          <button
            className="bulk-btn-primary"
            onClick={startBulkSync}
            disabled={isProcessing || selectedFiles.length === 0}
          >
            {isProcessing
              ? `Processing (${progress}%) - Do not close this tab`
              : `Start Bulk Upload & DB Sync (${selectedFiles.length} files)`}
          </button>
        </div>

        {/* Live Logs Card */}
        <div className="bulk-card">
          <h3 style={{ margin: "0 0 12px 0", fontSize: "1rem", color: "#d4af37" }}>
            Live Processing Log
          </h3>
          <div className="bulk-log-container" ref={logContainerRef}>
            {logs.length === 0 ? (
              <div style={{ color: "#666" }}>Ready. Select photos to begin.</div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className={`bulk-log-item bulk-log-${log.type}`}>
                  <span>[{log.timestamp}]</span>
                  <span>{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
